/**
 * Opening Balances — central review & close dashboard.
 *
 * One singleton workflow per company. The user seeds opening positions across
 * dealers / suppliers / loans / employees / GL accounts / inventory before
 * BizOP went live, watches the master journal entry assemble itself in real
 * time, and finally clicks "Close opening balance" — which posts the master
 * entry and locks every opening field across the app.
 *
 * Mock-vs-live data split
 * -----------------------
 * In mock mode (the default until InstantDB is wired):
 *   - read seed data from `mockOpeningBalanceConfig` and `mockOpeningEntries`,
 *   - mutations persist to local React state (status flip, asOfDate change,
 *     account-level opening upsert),
 *   - totals are recomputed from local rows so the OBE offset stays balanced.
 * In live mode the same component would swap reads to InstantDB queries
 * and call InstantDB transactions for mutations.
 * The view-model shape (`EntryRow`, `Totals`, `Status`) deliberately mirrors
 * the InstantDB query return so the swap is a search-and-replace.
 *
 * Closing flow correctness
 * ------------------------
 * `config.status` drives every editability gate: the date picker, the
 * GL "Set opening balance" buttons, and the Close button itself all read
 * `status === "draft"`. When status flips to closed/posted, the page
 * displays a green banner and no further mutations from this surface fire.
 * Sibling entity edit pages (dealer / supplier / loan / employee detail
 * views) own their own disabling — they should query the same `getConfig`
 * and gate their opening-balance form fields off `status !== "draft"`.
 */

import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowRight,
  BadgeDollarSign,
  Briefcase,
  Building2,
  Check,
  CheckCircle2,
  ChevronDown,
  Info,
  Landmark,
  Layers,
  Lock,
  Package,
  Pencil,
  Plus,
  Receipt,
  Sparkles,
  Trash2,
  UserCog,
  Users,
  Wallet,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Input,
  KpiCard,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TooltipContent,
  TooltipPortal,
  TooltipProvider,
  TooltipRoot,
  TooltipTrigger,
  cn,
  toast,
  type DataTableColumn,
} from "@bizop/ui-kit";
import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";

import { PageHeader } from "../../../components/PageHeader";
import { useMockData } from "../../../lib/mockMode";
import {
  mockAccounts,
  mockOpeningBalanceConfig,
  mockOpeningEntries,
  type MockAccount,
  type MockAccountType,
  type MockNormalBalance,
  type MockOpeningBalanceConfig,
  type MockOpeningEntryRow,
} from "../../../lib/glMock";
import {
  mockOpeningStockEntries,
  mockProducts,
  mockWarehouses,
  type MockOpeningStock,
  type MockProduct,
  type MockWarehouse,
} from "../../../lib/inventoryMock";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  OpeningStockForm,
  type OpeningStockSubmit,
} from "../../../components/forms/OpeningStockForm";

export const Route = createFileRoute("/_app/settings/opening-balances")({
  component: OpeningBalancesPage,
});

/* ─────────────────────────────────────────────────────────────
 * View-model — mirrors the InstantDB query return so swapping to live
 * data is a one-shot edit.
 * ───────────────────────────────────────────────────────────── */

type Status = "draft" | "posted" | "closed";

type EntryRow = {
  id: string;
  label: string;
  sublabel: string | null;
  asOf: number;
  signedBaseCents: bigint;
  debitInBaseCents: bigint;
  creditInBaseCents: bigint;
  /** Nav target for the "Edit" deep-link; null when no detail page exists. */
  detailHref: string | null;
  /** GL account (used for trial balance grouping + quick-add fallback). */
  accountId: string;
  accountCode: string;
  accountName: string;
};

type EntryTab = {
  key: "dealers" | "suppliers" | "loans" | "employees" | "accounts" | "inventory";
  label: string;
  icon: LucideIcon;
  emptyTitle: string;
  emptyDescription: string;
};

const TABS: EntryTab[] = [
  {
    key: "dealers",
    label: "Dealers",
    icon: Users,
    emptyTitle: "No dealer opening balances yet",
    emptyDescription:
      "Add one from any dealer's edit page — the entry shows up here once it's saved.",
  },
  {
    key: "suppliers",
    label: "Suppliers",
    icon: Briefcase,
    emptyTitle: "No supplier opening balances yet",
    emptyDescription:
      "Open any supplier and capture what you owed them on day one.",
  },
  {
    key: "loans",
    label: "Loans",
    icon: Landmark,
    emptyTitle: "No loans carried over",
    emptyDescription:
      "Existing term loans should be added under Finance → Loans with their day-one principal.",
  },
  {
    key: "employees",
    label: "Employees",
    icon: UserCog,
    emptyTitle: "No employee advances yet",
    emptyDescription:
      "Open an employee and record any salary advance still outstanding.",
  },
  {
    key: "accounts",
    label: "GL Accounts",
    icon: BadgeDollarSign,
    emptyTitle: "No cash, bank or other GL openings",
    emptyDescription:
      "Use the chart of accounts or the quick-add below to set cash on hand, bank balances, prepaid rent, etc.",
  },
  {
    key: "inventory",
    label: "Inventory",
    icon: Package,
    emptyTitle: "No inventory openings",
    emptyDescription:
      "Inventory openings can be seeded from the warehouse stock-take import flow.",
  },
];

/* ─────────────────────────────────────────────────────────────
 * Mock-mode helpers
 *
 * In live mode these are unused (replaced by the `useQuery` calls). They
 * exist so the page is fully browseable on a clean clone.
 * ───────────────────────────────────────────────────────────── */

type MockState = {
  config: MockOpeningBalanceConfig;
  rows: MockOpeningEntryRow[];
};

function categoryFor(row: MockOpeningEntryRow): EntryTab["key"] {
  return row.category === "account"
    ? "accounts"
    : row.category === "dealer"
      ? "dealers"
      : row.category === "supplier"
        ? "suppliers"
        : row.category === "loan"
          ? "loans"
          : row.category === "employee"
            ? "employees"
            : "inventory";
}

function detailHrefFor(row: MockOpeningEntryRow): string | null {
  switch (row.category) {
    case "dealer":
      return row.sourceId ? `/sales/dealers/${row.sourceId}` : "/sales/dealers";
    case "supplier":
      return "/imports/purchase-orders"; // suppliers live under imports today
    case "loan":
      return "/finance/loans";
    case "employee":
      return row.sourceId
        ? `/workforce/employees/${row.sourceId}`
        : "/workforce/employees";
    case "account":
      return "/finance/accounts";
    case "inventory":
      return "/inventory";
    default:
      return null;
  }
}

function rowSublabel(
  row: MockOpeningEntryRow,
  account: MockAccount | undefined,
): string {
  switch (row.category) {
    case "dealer":
      return "Accounts Receivable";
    case "supplier":
      return "Accounts Payable";
    case "loan":
      return "Loans Payable — principal";
    case "employee":
      return "Employee advance";
    case "inventory":
      return "Stock at cost";
    case "account":
      return account
        ? `${account.accountType.charAt(0).toUpperCase()}${account.accountType.slice(1)}`
        : "GL account";
    default:
      return "";
  }
}

function rowLabel(row: MockOpeningEntryRow, account: MockAccount | undefined): string {
  if (row.category === "account" && account) {
    return `${account.code} — ${account.name}`;
  }
  return row.label;
}

function toEntryRow(
  row: MockOpeningEntryRow,
  asOf: number,
  accountById: Map<string, MockAccount>,
): EntryRow {
  const account = accountById.get(row.accountId);
  const debit = row.signedBaseCents > 0n ? row.signedBaseCents : 0n;
  const credit = row.signedBaseCents < 0n ? -row.signedBaseCents : 0n;
  return {
    id: row.lineId,
    label: rowLabel(row, account),
    sublabel: rowSublabel(row, account),
    asOf,
    signedBaseCents: row.signedBaseCents,
    debitInBaseCents: debit,
    creditInBaseCents: credit,
    detailHref: detailHrefFor(row),
    accountId: row.accountId,
    accountCode: row.accountCode,
    accountName: account?.name ?? row.accountCode,
  };
}

function computeTotals(rows: MockOpeningEntryRow[]): {
  debitsInBaseCents: bigint;
  creditsInBaseCents: bigint;
  lineCount: number;
  isBalanced: boolean;
} {
  let nonObeDebits = 0n;
  let nonObeCredits = 0n;
  for (const r of rows) {
    if (r.signedBaseCents > 0n) nonObeDebits += r.signedBaseCents;
    else if (r.signedBaseCents < 0n) nonObeCredits += -r.signedBaseCents;
  }
  // OBE offset balances the entry — same logic as the backend helper.
  const obeCredit =
    nonObeDebits > nonObeCredits ? nonObeDebits - nonObeCredits : 0n;
  const obeDebit =
    nonObeCredits > nonObeDebits ? nonObeCredits - nonObeDebits : 0n;
  const debits = nonObeDebits + obeDebit;
  const credits = nonObeCredits + obeCredit;
  // +1 for the OBE line if there's any residual.
  const obeLines = obeCredit > 0n || obeDebit > 0n ? 1 : 0;
  return {
    debitsInBaseCents: debits,
    creditsInBaseCents: credits,
    lineCount: rows.length + obeLines,
    isBalanced: debits === credits,
  };
}

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function OpeningBalancesPage() {
  const isMock = useMockData();

  // Persistent (dual-backed) reads for chart-of-accounts and inventory
  // catalog. These flow through InstantDB when configured; otherwise they
  // fall back to localStorage-backed mocks.
  const [accounts] = usePersistentMockState<MockAccount[]>(
    "accounts",
    mockAccounts,
  );
  const [products] = usePersistentMockState<MockProduct[]>(
    "products",
    mockProducts,
  );
  const [warehouses] = usePersistentMockState<MockWarehouse[]>(
    "warehouses",
    mockWarehouses,
  );

  // -------- MOCK MODE ---------
  // In live mode we'd substitute:
  //   const data = useQuery(api.finance.opening.getConfig);
  //   const entries = useQuery(api.finance.opening.listEntries);
  //   const setAsOfDate = useMutation(api.finance.opening.setAsOfDate);
  //   const setOpeningBalance = useMutation(api.finance.opening.setOpeningBalance);
  //   const closeOpeningBalance = useMutation(api.finance.opening.closeOpeningBalance);
  // and remove the local `mock` state.
  const [mock, setMock] = useState<MockState>(() => ({
    config: { ...mockOpeningBalanceConfig },
    rows: isMock ? [...mockOpeningEntries] : [],
  }));

  const [openingStock, setOpeningStock] = useState<MockOpeningStock[]>(() =>
    isMock ? [...mockOpeningStockEntries] : [],
  );

  const accountById = useMemo(
    () => new Map(accounts.map((a) => [a._id, a])),
    [accounts],
  );

  const status: Status = mock.config.status;
  const asOfDate = mock.config.asOfDate;
  const isDraft = status === "draft";

  // Categorised view (mirrors `api.finance.opening.listEntries` shape).
  const entriesByTab = useMemo(() => {
    const buckets: Record<EntryTab["key"], EntryRow[]> = {
      dealers: [],
      suppliers: [],
      loans: [],
      employees: [],
      accounts: [],
      inventory: [],
    };
    for (const row of mock.rows) {
      buckets[categoryFor(row)].push(toEntryRow(row, asOfDate, accountById));
    }
    // Sort each bucket: largest absolute amount first so the user reviews
    // material balances at the top.
    for (const k of Object.keys(buckets) as EntryTab["key"][]) {
      buckets[k].sort((a, b) => {
        const aa = a.signedBaseCents < 0n ? -a.signedBaseCents : a.signedBaseCents;
        const bb = b.signedBaseCents < 0n ? -b.signedBaseCents : b.signedBaseCents;
        return Number(bb - aa);
      });
    }
    return buckets;
  }, [mock.rows, asOfDate, accountById]);

  const totals = useMemo(() => computeTotals(mock.rows), [mock.rows]);

  // ----- Reopen flow (unlock a closed / posted opening balance for editing) -----

  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenConfirmText, setReopenConfirmText] = useState("");

  function handleReopenConfirmed() {
    setMock((s) => ({
      ...s,
      config: { ...s.config, status: "draft", closedAt: null },
    }));
    setReopenDialogOpen(false);
    setReopenConfirmText("");
    toast.success(
      "Opening balances reopened for editing. Remember to close them again when you're done.",
    );
  }

  // ----- Mutations (mock implementations; mirror the InstantDB contracts) -----

  function handleSetAsOfDate(next: number) {
    if (!isDraft) {
      toast.error("Can't change as-of date — reopen opening balances first.");
      return;
    }
    setMock((s) => ({ ...s, config: { ...s.config, asOfDate: next } }));
    toast.success("As-of date updated.");
  }

  function handleBootstrap() {
    // Hero state primary CTA — identical to calling
    // setAsOfDate({ asOfDate: <today> }).
    const today = startOfDay(Date.now());
    setMock((s) => ({
      ...s,
      config: { ...s.config, asOfDate: today, status: "draft" },
    }));
    toast.success("Opening balance workflow started.");
  }

  function handleSetOpeningBalance(args: {
    accountId: string;
    balanceCents: bigint;
    asOfDate?: number;
    description?: string;
  }) {
    if (!isDraft) return;
    const acc = accountById.get(args.accountId);
    if (!acc) {
      toast.error("Account not found.");
      return;
    }
    if (
      acc.accountType !== "asset" &&
      acc.accountType !== "liability" &&
      acc.accountType !== "equity"
    ) {
      toast.error(
        `Account ${acc.code} is ${acc.accountType} — only asset / liability / equity accounts may carry an opening balance.`,
      );
      return;
    }
    setMock((s) => {
      const lineId = `obl_acc_${acc._id}`;
      const next = s.rows.filter((r) => r.lineId !== lineId);
      if (args.balanceCents !== 0n) {
        next.push({
          lineId,
          category: "account",
          label: `${acc.code} — ${acc.name}`,
          sourceId: acc._id,
          accountId: acc._id,
          accountCode: acc.code,
          signedBaseCents: args.balanceCents,
        });
      }
      return { ...s, rows: next };
    });
    toast.success(`Opening balance saved for ${acc.code} ${acc.name}.`);
  }

  function handleSetOpeningStock(args: OpeningStockSubmit) {
    if (!isDraft) return;

    const product = products.find((p) => p._id === args.productId);
    const warehouse = warehouses.find((w) => w._id === args.warehouseId);
    if (!product || !warehouse) {
      toast.error("Product or warehouse not found.");
      return;
    }

    const lineId = `obl_inv_${args.productId}_${args.warehouseId}`;
    const label = `${product.displayName} @ ${warehouse.displayName}`;

    // Update the raw opening stock list
    setOpeningStock((prev) => {
      const filtered = prev.filter(
        (s) =>
          !(
            s.productId === args.productId && s.warehouseId === args.warehouseId
          ),
      );
      return [
        ...filtered,
        {
          lineId,
          productId: args.productId,
          warehouseId: args.warehouseId,
          quantityOnHand: args.quantityOnHand,
          unitCostBaseCents: args.unitCostBaseCents,
          totalValueBaseCents: args.totalValueBaseCents,
          asOfDate: args.asOfDate,
        },
      ];
    });

    // Update the GL entry rows (for the trial balance and totals)
    setMock((prev) => {
      const filtered = prev.rows.filter((r) => r.lineId !== lineId);
      return {
        ...prev,
        rows: [
          ...filtered,
          {
            lineId,
            category: "inventory" as const,
            label,
            sourceId: lineId,
            accountId: "acc_1300",
            accountCode: "1300",
            signedBaseCents: args.totalValueBaseCents,
          },
        ],
      };
    });

    toast.success(`Opening stock saved for ${product.displayName}.`);
  }

  function handleRemoveOpeningStock(lineId: string) {
    if (!isDraft) return;
    setOpeningStock((prev) =>
      prev.filter(
        (s) => `obl_inv_${s.productId}_${s.warehouseId}` !== lineId,
      ),
    );
    setMock((prev) => ({
      ...prev,
      rows: prev.rows.filter((r) => r.lineId !== lineId),
    }));
    toast.success("Opening stock entry removed.");
  }

  function handleCloseOpeningBalance() {
    if (!isDraft) {
      toast.error("Already closed.");
      return;
    }
    if (!totals.isBalanced) {
      toast.error("Debits and credits must balance before closing.");
      return;
    }
    setMock((s) => ({
      ...s,
      config: { ...s.config, status: "closed", closedAt: Date.now() },
    }));
    toast.success("Opening balance closed.");
  }

  // ----- Hero / empty state -----

  // Foundation backend returns `null` when the singleton hasn't been
  // bootstrapped. In mock mode we mirror that by treating an "uninitialised"
  // config as one with status === "draft" but no rows AND no closed flag —
  // BUT to demo the hero state cleanly we expose an explicit reset button.
  const showHero =
    isMock &&
    !mock.config.openingJournalEntryId &&
    mock.rows.length === 0 &&
    !asOfDate;

  if (showHero) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Opening Balances"
          description="Seed your starting position from before BizOP went live."
        />
        <HeroEmptyState onBegin={handleBootstrap} />
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Opening Balances"
          description="Seed your starting position from before BizOP went live."
          actions={
            <div className="flex items-center gap-2">
              <StatusBadge status={status} />
              <DatePickerInput
                value={asOfDate}
                disabled={!isDraft}
                onChange={handleSetAsOfDate}
              />
              <CloseAction
                disabled={!isDraft || !totals.isBalanced}
                isBalanced={totals.isBalanced}
                isDraft={isDraft}
                onConfirm={handleCloseOpeningBalance}
              />
            </div>
          }
        />

        <StatusBanner
          status={status}
          closedAt={mock.config.closedAt}
          onReopen={() => setReopenDialogOpen(true)}
        />

        {/* --- Summary cards --- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            caption="Total debits"
            value={formatMoneySmart(totals.debitsInBaseCents, "BDT")}
          />
          <KpiCard
            caption="Total credits"
            value={formatMoneySmart(totals.creditsInBaseCents, "BDT")}
          />
          <DifferenceCard
            debits={totals.debitsInBaseCents}
            credits={totals.creditsInBaseCents}
          />
          <KpiCard
            caption="Entries seeded"
            value={String(totals.lineCount)}
            hint={
              totals.lineCount === 0
                ? "Nothing seeded yet — add openings from any entity edit page."
                : `Across ${countNonEmptyTabs(entriesByTab)} categories.`
            }
          />
        </div>

        {/* --- Tabbed body --- */}
        <Tabs defaultValue={TABS[0]?.key ?? "dealers"} className="w-full">
          <TabsList aria-label="Opening balance categories">
            {TABS.map((t) => {
              const Icon = t.icon;
              const count = entriesByTab[t.key].length;
              return (
                <TabsTrigger key={t.key} value={t.key}>
                  <Icon aria-hidden className="size-3.5" />
                  <span>{t.label}</span>
                  {count > 0 ? (
                    <Badge variant="neutral" size="sm" className="ml-1.5">
                      {count}
                    </Badge>
                  ) : null}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {TABS.map((t) => (
            <TabsContent key={t.key} value={t.key} className="mt-4">
              {t.key === "inventory" ? (
                <InventoryTab
                  rows={entriesByTab["inventory"]}
                  openingStock={openingStock}
                  products={products}
                  warehouses={warehouses}
                  isDraft={isDraft}
                  onSet={handleSetOpeningStock}
                  onRemove={handleRemoveOpeningStock}
                />
              ) : (
                <CategoryTab
                  tab={t}
                  rows={entriesByTab[t.key]}
                  isDraft={isDraft}
                  onSetOpeningBalance={
                    t.key === "accounts" ? handleSetOpeningBalance : undefined
                  }
                />
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* --- Unified chart of accounts opening balances --- */}
        <UnifiedAccountOpenings
          accounts={accounts}
          existingRows={mock.rows}
          isDraft={isDraft}
          onSetOpeningBalance={handleSetOpeningBalance}
        />

        {/* --- Trial balance preview --- */}
        <TrialBalancePreview
          rows={mock.rows}
          totals={totals}
          accountById={accountById}
        />

        {/* --- Reopen confirmation dialog (double confirmation) --- */}
        <Dialog
          open={reopenDialogOpen}
          onOpenChange={(open) => {
            if (!open) {
              setReopenDialogOpen(false);
              setReopenConfirmText("");
            }
          }}
        >
          <DialogContent size="md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="size-5 text-(--warning)" />
                Reopen opening balances for editing?
              </DialogTitle>
              <DialogDescription>
                <span className="block mb-2">
                  Opening balances have been finalized. Editing them will affect
                  your financial statements, trial balance, and all downstream
                  reports.
                </span>
                <span className="block mb-3 font-medium text-(--text-primary)">
                  Are you sure you want to proceed? Type{" "}
                  <span className="font-mono text-(--danger)">REOPEN</span> below
                  to confirm.
                </span>
                <Input
                  value={reopenConfirmText}
                  onChange={(e) => setReopenConfirmText(e.target.value)}
                  placeholder="Type REOPEN to confirm"
                  className="font-mono"
                />
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setReopenDialogOpen(false);
                  setReopenConfirmText("");
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={reopenConfirmText.trim().toUpperCase() !== "REOPEN"}
                onClick={handleReopenConfirmed}
              >
                Reopen for editing
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Header — status badge, date picker, close button
 * ───────────────────────────────────────────────────────────── */

function StatusBadge({ status }: { status: Status }) {
  if (status === "draft") {
    return (
      <Badge variant="warning" size="md">
        Draft
      </Badge>
    );
  }
  if (status === "posted") {
    return (
      <Badge variant="info" size="md">
        Posted
      </Badge>
    );
  }
  return (
    <Badge variant="success" size="md">
      <CheckCircle2 aria-hidden className="size-3" />
      Closed
    </Badge>
  );
}

function DatePickerInput({
  value,
  disabled,
  onChange,
}: {
  value: number;
  disabled: boolean;
  onChange: (next: number) => void;
}) {
  const iso = toIsoDate(value);
  return (
    <div className="flex items-center gap-1.5">
      <Label
        htmlFor="opening-as-of"
        className="text-[12px] text-(--text-tertiary)"
      >
        As of
      </Label>
      <Input
        id="opening-as-of"
        type="date"
        value={iso}
        disabled={disabled}
        onChange={(e) => {
          const v = e.target.value;
          if (!v) return;
          const ms = fromIsoDate(v);
          if (Number.isFinite(ms)) onChange(ms);
        }}
        inputSize="sm"
        className="font-mono"
      />
    </div>
  );
}

function CloseAction({
  disabled,
  isDraft,
  isBalanced,
  onConfirm,
}: {
  disabled: boolean;
  isDraft: boolean;
  isBalanced: boolean;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // The button is "disabled" visually when out of balance, but we leave the
  // click handler in place so the tooltip works on touch devices.
  const tooltipMessage = !isDraft
    ? "Opening balances already closed."
    : !isBalanced
      ? "Debits and credits must balance before closing."
      : null;

  const triggerButton = (
    <Button
      type="button"
      variant="default"
      onClick={() => {
        if (disabled) return;
        setOpen(true);
      }}
      aria-disabled={disabled}
      className={cn(disabled && "opacity-50 cursor-not-allowed")}
    >
      <Lock className="size-4" />
      Close opening balance
    </Button>
  );

  async function handleConfirm() {
    setBusy(true);
    try {
      await Promise.resolve(onConfirm());
      setOpen(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {tooltipMessage ? (
        <TooltipRoot delayDuration={150}>
          <TooltipTrigger asChild>
            <span>{triggerButton}</span>
          </TooltipTrigger>
          <TooltipPortal>
            <TooltipContent side="bottom">{tooltipMessage}</TooltipContent>
          </TooltipPortal>
        </TooltipRoot>
      ) : (
        triggerButton
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Close opening balances?</DialogTitle>
            <DialogDescription>
              This posts the master journal entry and locks all opening
              fields. Existing entries can no longer be edited; future
              corrections must go through normal journal entries.
              <span className="mt-2 block font-medium text-(--text-primary)">
                This action is final.
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={handleConfirm}
              loading={busy}
            >
              Close opening balance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Status banners — different copy per workflow phase
 * ───────────────────────────────────────────────────────────── */

function StatusBanner({
  status,
  closedAt,
  onReopen,
}: {
  status: Status;
  closedAt: number | null;
  onReopen?: () => void;
}) {
  if (status === "draft") {
    return (
      <Banner variant="info" icon={Info}>
        These balances are still draft. Edit anywhere in the app, then close
        when everything checks out.
      </Banner>
    );
  }
  const date = closedAt ? formatDate(closedAt, { style: "long" }) : "—";
  return (
    <div
      role="status"
      className={cn(
        "flex items-start justify-between gap-2.5 rounded-md border px-3 py-2.5",
        "border-(--success-soft) bg-(--success-soft)/40 text-(--success)",
      )}
    >
      <div className="flex items-start gap-2.5">
        <CheckCircle2 className="mt-0.5 size-4 shrink-0" aria-hidden />
        <p className="text-[13px] leading-snug text-(--text-primary)">
          Opening balances closed on {date}. All opening fields are now locked.
        </p>
      </div>
      {onReopen ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onReopen}
          className="shrink-0"
        >
          <Pencil className="size-3.5" />
          Reopen for editing
        </Button>
      ) : null}
    </div>
  );
}

function Banner({
  variant,
  icon: Icon,
  children,
}: {
  variant: "info" | "success";
  icon: LucideIcon;
  children: React.ReactNode;
}) {
  const accent =
    variant === "success"
      ? "border-(--success-soft) bg-(--success-soft)/40 text-(--success)"
      : "border-(--info-soft) bg-(--info-soft)/40 text-(--info)";
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2.5 rounded-md border px-3 py-2.5",
        accent,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p className="text-[13px] leading-snug text-(--text-primary)">
        {children}
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Difference card — green when 0, red otherwise
 * ───────────────────────────────────────────────────────────── */

function DifferenceCard({
  debits,
  credits,
}: {
  debits: bigint;
  credits: bigint;
}) {
  const diff = debits - credits;
  const balanced = diff === 0n;
  const abs = diff < 0n ? -diff : diff;
  const sign = diff > 0n ? "+" : diff < 0n ? "−" : "";
  return (
    <KpiCard
      caption="Difference"
      value={
        <span
          className={cn(
            balanced
              ? "text-(--success)"
              : "text-(--danger)",
          )}
        >
          {sign}
          {formatMoneySmart(abs, "BDT")}
        </span>
      }
      hint={
        balanced ? (
          <span className="inline-flex items-center gap-1 text-(--success)">
            <Check className="size-3" />
            Debits = credits.
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-(--danger)">
            <AlertTriangle className="size-3" />
            Out of balance by ৳{formatMoneySmart(abs, "BDT", { display: "none" })}.
          </span>
        )
      }
    />
  );
}

/* ─────────────────────────────────────────────────────────────
 * Tab body — DataTable with category-specific empty state. The
 * GL Accounts tab also renders the quick-add launcher.
 * ───────────────────────────────────────────────────────────── */

function CategoryTab({
  tab,
  rows,
  isDraft,
  onSetOpeningBalance,
}: {
  tab: EntryTab;
  rows: EntryRow[];
  isDraft: boolean;
  onSetOpeningBalance?: (args: {
    accountId: string;
    balanceCents: bigint;
    asOfDate?: number;
    description?: string;
  }) => void;
}) {
  const Icon = tab.icon;

  const columns = useMemo<DataTableColumn<EntryRow>[]>(
    () => [
      {
        id: "label",
        header: "Label",
        cell: (info) => (
          <div className="min-w-0">
            <div className="truncate text-(--text-primary) font-medium">
              {info.row.original.label}
            </div>
            {info.row.original.sublabel ? (
              <div className="truncate text-[12px] text-(--text-tertiary)">
                {info.row.original.sublabel}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        id: "asOf",
        header: "As of",
        cell: (info) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {formatDate(info.row.original.asOf, { style: "medium" })}
          </span>
        ),
      },
      {
        id: "amount",
        header: "Signed amount",
        meta: { numeric: true, mono: true },
        cell: (info) => <SignedAmountCell row={info.row.original} />,
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const r = info.row.original;
          return (
            <div className="flex justify-end">
              {r.detailHref ? (
                <Button
                  asChild
                  variant="ghost"
                  size="sm"
                  aria-label={`Edit ${r.label}`}
                >
                  <Link to={r.detailHref}>
                    <Pencil className="size-3.5" />
                    <span className="ml-1 hidden md:inline">Edit</span>
                  </Link>
                </Button>
              ) : null}
            </div>
          );
        },
      },
    ],
    [],
  );

  const showQuickAdd = tab.key === "accounts" && Boolean(onSetOpeningBalance);

  return (
    <div className="flex flex-col gap-3">
      {showQuickAdd ? (
        <div className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Sparkles className="size-4 text-(--text-tertiary)" />
            <div className="text-[12px] text-(--text-secondary)">
              Quick-add: cash, bank, prepaid, or other balance-sheet account.
            </div>
          </div>
          <QuickAddAccountOpeningButton
            disabled={!isDraft}
            onSubmit={onSetOpeningBalance!}
          />
        </div>
      ) : null}

      <DataTable
        data={rows}
        columns={columns}
        emptyTitle={tab.emptyTitle}
        emptyDescription={tab.emptyDescription}
        emptyIcon={<Icon className="size-5 text-(--text-tertiary)" />}
      />
    </div>
  );
}

function InventoryTab({
  rows,
  openingStock,
  products,
  warehouses,
  isDraft,
  onSet,
  onRemove,
}: {
  rows: EntryRow[];
  openingStock: MockOpeningStock[];
  products: MockProduct[];
  warehouses: MockWarehouse[];
  isDraft: boolean;
  onSet: (args: OpeningStockSubmit) => void;
  onRemove: (lineId: string) => void;
}) {
  const productById = useMemo(
    () => new Map(products.map((p) => [p._id, p])),
    [products],
  );
  const warehouseById = useMemo(
    () => new Map(warehouses.map((w) => [w._id, w])),
    [warehouses],
  );
  const productOptions = useMemo(
    () =>
      products.map((p) => ({
        _id: p._id,
        displayName: p.displayName,
        sku: p.sku,
      })),
    [products],
  );
  const warehouseOptions = useMemo(
    () =>
      warehouses.map((w) => ({
        _id: w._id,
        displayName: w.displayName,
        code: w.code,
      })),
    [warehouses],
  );

  const columns = useMemo<DataTableColumn<MockOpeningStock>[]>(
    () => [
      {
        id: "product",
        header: "Product",
        cell: (info) => {
          const product = productById.get(info.row.original.productId);
          const wh = warehouseById.get(info.row.original.warehouseId);
          return (
            <div className="min-w-0">
              <div className="truncate text-(--text-primary) font-medium">
                {product?.displayName ?? info.row.original.productId}
              </div>
              <div className="truncate text-[12px] text-(--text-tertiary)">
                {product?.sku} · {wh?.displayName ?? info.row.original.warehouseId}
              </div>
            </div>
          );
        },
      },
      {
        id: "qty",
        header: "Qty",
        meta: { numeric: true, mono: true },
        cell: (info) => (
          <span className="font-mono tabular-nums">
            {info.row.original.quantityOnHand.toLocaleString()}
          </span>
        ),
      },
      {
        id: "unitCost",
        header: "Unit cost",
        meta: { numeric: true, mono: true },
        cell: (info) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {formatMoney(info.row.original.unitCostBaseCents, "BDT")}
          </span>
        ),
      },
      {
        id: "total",
        header: "Total value",
        meta: { numeric: true, mono: true },
        cell: (info) => (
          <span className="font-mono tabular-nums font-medium text-(--text-primary)">
            {formatMoney(info.row.original.totalValueBaseCents, "BDT")}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        cell: (info) => {
          const lineId = `obl_inv_${info.row.original.productId}_${info.row.original.warehouseId}`;
          return (
            <div className="flex justify-end gap-1">
              <OpeningStockForm
                products={productOptions}
                warehouses={warehouseOptions}
                existingEntry={info.row.original}
                disabled={!isDraft}
                onSubmit={onSet}
                trigger={
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Edit"
                    disabled={!isDraft}
                  >
                    <Pencil className="size-4" />
                  </Button>
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove"
                disabled={!isDraft}
                onClick={() => onRemove(lineId)}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          );
        },
      },
    ],
    [productById, warehouseById, productOptions, warehouseOptions, isDraft, onSet, onRemove],
  );

  // Suppress unused warning for `rows` — it feeds the trial balance via
  // the parent mock state but the InventoryTab renders `openingStock` directly
  // since it carries the richer product×warehouse fields.
  void rows;

  return (
    <div className="flex flex-col gap-3">
      {isDraft ? (
        <div className="flex items-center justify-between rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2.5">
          <div className="flex items-center gap-2">
            <Package className="size-4 text-(--text-tertiary)" />
            <div className="text-[12px] text-(--text-secondary)">
              Set the quantity on hand for each SKU at your go-live date.
            </div>
          </div>
          <OpeningStockForm
            products={productOptions}
            warehouses={warehouseOptions}
            disabled={!isDraft}
            onSubmit={onSet}
            trigger={
              <Button variant="secondary" size="sm" disabled={!isDraft}>
                <Plus className="size-3.5" />
                Add opening stock
              </Button>
            }
          />
        </div>
      ) : null}

      {openingStock.length === 0 ? (
        <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) p-8 text-center">
          <Package className="mx-auto size-8 text-(--text-tertiary) mb-2" />
          <div className="text-[13px] font-medium text-(--text-primary)">
            No inventory openings yet
          </div>
          <div className="text-[12px] text-(--text-tertiary) mt-1">
            Add stock positions above — each entry creates a DR Inventory / CR
            OBE journal line.
          </div>
        </div>
      ) : (
        <DataTable
          data={openingStock}
          columns={columns}
          emptyTitle="No inventory openings"
          emptyDescription="Add a stock position using the button above."
        />
      )}

      {openingStock.length > 0 ? (
        <div className="flex items-center justify-end gap-3 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-3 py-2">
          <span className="text-[12px] text-(--text-secondary)">
            {openingStock.length}{" "}
            {openingStock.length === 1 ? "item" : "items"}
          </span>
          <span className="text-[12px] font-medium text-(--text-primary) font-mono">
            Total:{" "}
            {formatMoney(
              openingStock.reduce(
                (acc, s) => acc + s.totalValueBaseCents,
                0n,
              ),
              "BDT",
            )}
          </span>
        </div>
      ) : null}
    </div>
  );
}

function SignedAmountCell({ row }: { row: EntryRow }) {
  const isDebit = row.signedBaseCents > 0n;
  const isCredit = row.signedBaseCents < 0n;
  const amt = row.signedBaseCents < 0n ? -row.signedBaseCents : row.signedBaseCents;

  // Color semantics
  //   - Dealer / employee / asset (positive sign on the LINE = debit) → success-ish
  //     because that's "owed to us / asset on the books".
  //   - Supplier / loan (negative sign on the line = credit) → danger because
  //     the company is on the hook for it.
  // We keep this tab-agnostic so the same row in any context renders the same.
  const tone = isDebit ? "text-(--success)" : isCredit ? "text-(--danger)" : "text-(--text-tertiary)";

  return (
    <span className={cn("font-mono tabular-nums", tone)}>
      {isCredit ? "−" : ""}
      {formatMoney(amt, "BDT")}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Quick-add dialog — set opening balance for any GL account
 * ───────────────────────────────────────────────────────────── */

const ACCOUNT_TYPES_FOR_QUICK_ADD: ReadonlyArray<MockAccountType> = [
  "asset",
  "liability",
  "equity",
];

function QuickAddAccountOpeningButton({
  disabled,
  onSubmit,
}: {
  disabled: boolean;
  onSubmit: (args: {
    accountId: string;
    balanceCents: bigint;
    asOfDate?: number;
    description?: string;
  }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [accountId, setAccountId] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [asOf, setAsOf] = useState<string>(toIsoDate(Date.now()));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [accounts] = usePersistentMockState<MockAccount[]>(
    "accounts",
    mockAccounts,
  );

  const eligible = useMemo(
    () =>
      accounts
        .filter((a) =>
          ACCOUNT_TYPES_FOR_QUICK_ADD.includes(a.accountType) && a.isActive,
        )
        .sort((a, b) => a.code.localeCompare(b.code)),
    [accounts],
  );

  const selected = eligible.find((a) => a._id === accountId);
  const normalSide: MockNormalBalance | null = selected?.normalBalance ?? null;

  function reset() {
    setAccountId("");
    setAmount("");
    setDescription("");
    setAsOf(toIsoDate(Date.now()));
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!accountId) {
      setError("Pick an account.");
      return;
    }
    const cents = parseDecimalToCents(amount);
    if (cents === null) {
      setError("Enter a number, e.g. 250000 or -125000.");
      return;
    }
    setSubmitting(true);
    try {
      onSubmit({
        accountId,
        balanceCents: cents,
        asOfDate: fromIsoDate(asOf),
        description: description.trim() ? description.trim() : undefined,
      });
      setOpen(false);
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submit failed.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) reset();
      }}
    >
      <Button
        type="button"
        variant="secondary"
        size="sm"
        disabled={disabled}
        onClick={() => setOpen(true)}
      >
        <Wallet className="size-3.5" />
        Set opening balance
      </Button>
      <DialogContent size="md">
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Set opening balance</DialogTitle>
            <DialogDescription>
              Captures the day-one balance on a specific GL account. The OBE
              offset is auto-balanced behind the scenes.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qa-account">Account</Label>
              <select
                id="qa-account"
                value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className={cn(
                  "h-(--control-height) rounded-md border border-(--border-default) bg-(--bg-surface)",
                  "px-2.5 text-[13px] text-(--text-primary)",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--border-focus)",
                )}
              >
                <option value="">Pick an account…</option>
                {eligible.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.code} — {a.name} ({a.accountType})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="qa-amount">Amount (BDT)</Label>
                <Input
                  id="qa-amount"
                  inputMode="decimal"
                  placeholder="250000.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="font-mono"
                />
                {normalSide ? (
                  <div className="text-[11px] text-(--text-tertiary)">
                    Positive = {normalSide === "debit" ? "debit" : "credit"} on
                    this account ({normalSide === "debit" ? "natural for assets" : "natural for liabilities/equity"}).
                  </div>
                ) : null}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="qa-asof">As of</Label>
                <Input
                  id="qa-asof"
                  type="date"
                  value={asOf}
                  onChange={(e) => setAsOf(e.target.value)}
                  className="font-mono"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="qa-desc">Description (optional)</Label>
              <Input
                id="qa-desc"
                placeholder={
                  selected
                    ? `Opening balance — ${selected.code} ${selected.name}`
                    : "Opening balance — …"
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {error ? (
              <div role="alert" className="text-[12px] text-(--danger)">
                {error}
              </div>
            ) : null}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" loading={submitting}>
              Save opening balance
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Trial balance preview — collapsible, default-closed
 * ───────────────────────────────────────────────────────────── */

type TrialRow = {
  accountId: string;
  accountCode: string;
  accountName: string;
  debit: bigint;
  credit: bigint;
};

function TrialBalancePreview({
  rows,
  totals,
  accountById,
}: {
  rows: MockOpeningEntryRow[];
  totals: { debitsInBaseCents: bigint; creditsInBaseCents: bigint };
  accountById: Map<string, MockAccount>;
}) {
  const [open, setOpen] = useState(false);

  const trial = useMemo<TrialRow[]>(() => {
    const map = new Map<string, TrialRow>();
    for (const r of rows) {
      const acc = accountById.get(r.accountId);
      if (!acc) continue;
      const existing = map.get(r.accountId) ?? {
        accountId: r.accountId,
        accountCode: r.accountCode,
        accountName: acc.name,
        debit: 0n,
        credit: 0n,
      };
      if (r.signedBaseCents > 0n) existing.debit += r.signedBaseCents;
      else if (r.signedBaseCents < 0n) existing.credit += -r.signedBaseCents;
      map.set(r.accountId, existing);
    }
    return Array.from(map.values()).sort((a, b) =>
      a.accountCode.localeCompare(b.accountCode),
    );
  }, [rows, accountById]);

  // OBE balancing leg.
  let nonObeDebits = 0n;
  let nonObeCredits = 0n;
  for (const r of trial) {
    nonObeDebits += r.debit;
    nonObeCredits += r.credit;
  }
  const obeDebit = nonObeCredits > nonObeDebits ? nonObeCredits - nonObeDebits : 0n;
  const obeCredit = nonObeDebits > nonObeCredits ? nonObeDebits - nonObeCredits : 0n;

  const totalDebits = totals.debitsInBaseCents;
  const totalCredits = totals.creditsInBaseCents;

  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface)">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center justify-between gap-3 px-4 py-3 text-left",
          "hover:bg-(--bg-hover)/50 transition-colors rounded-t-lg",
        )}
      >
        <div className="flex items-center gap-2.5">
          <Layers className="size-4 text-(--text-tertiary)" />
          <div>
            <div className="text-[13px] font-semibold tracking-tight text-(--text-primary)">
              Trial balance preview
            </div>
            <div className="text-[12px] text-(--text-tertiary)">
              Per-account opening debit / credit, with the OBE offset row.
            </div>
          </div>
        </div>
        <ChevronDown
          className={cn(
            "size-4 text-(--text-tertiary) transition-transform duration-(--duration-fast)",
            open ? "rotate-180" : "rotate-0",
          )}
        />
      </button>
      {open ? (
        <div className="border-t border-(--border-subtle) overflow-x-auto">
          {trial.length === 0 ? (
            <EmptyState
              compact
              icon={<Receipt className="size-5" />}
              title="No opening lines yet"
              description="Add an opening balance from any tab to see the trial balance preview."
            />
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-(--border-subtle) bg-(--bg-canvas)">
                  <th className="px-4 py-2 text-left font-medium text-(--text-tertiary)">
                    Code
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-(--text-tertiary)">
                    Account
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-(--text-tertiary)">
                    Debit
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-(--text-tertiary)">
                    Credit
                  </th>
                </tr>
              </thead>
              <tbody>
                {trial.map((r) => (
                  <tr
                    key={r.accountId}
                    className="border-b border-(--border-subtle)/60"
                  >
                    <td className="px-4 py-2 font-mono text-(--text-secondary)">
                      {r.accountCode}
                    </td>
                    <td className="px-4 py-2 text-(--text-primary)">
                      {r.accountName}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-(--text-primary)">
                      {r.debit > 0n ? formatMoney(r.debit, "BDT") : "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-(--text-primary)">
                      {r.credit > 0n ? formatMoney(r.credit, "BDT") : "—"}
                    </td>
                  </tr>
                ))}
                {/* OBE balancing row — visually distinct so it's clearly the offset. */}
                {obeDebit > 0n || obeCredit > 0n ? (
                  <tr className="border-b border-(--border-subtle)/60 bg-(--bg-canvas)">
                    <td className="px-4 py-2 font-mono text-(--text-secondary)">
                      3999
                    </td>
                    <td className="px-4 py-2 text-(--text-primary)">
                      <span className="inline-flex items-center gap-1.5">
                        Opening Balance Equity
                        <Badge variant="neutral" size="sm">
                          offset
                        </Badge>
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-(--text-primary)">
                      {obeDebit > 0n ? formatMoney(obeDebit, "BDT") : "—"}
                    </td>
                    <td className="px-4 py-2 text-right font-mono tabular-nums text-(--text-primary)">
                      {obeCredit > 0n ? formatMoney(obeCredit, "BDT") : "—"}
                    </td>
                  </tr>
                ) : null}
                <tr className="bg-(--bg-canvas) font-semibold">
                  <td className="px-4 py-2.5"></td>
                  <td className="px-4 py-2.5 text-(--text-primary)">Total</td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-(--text-primary)">
                    {formatMoney(totalDebits, "BDT")}
                  </td>
                  <td className="px-4 py-2.5 text-right font-mono tabular-nums text-(--text-primary)">
                    {formatMoney(totalCredits, "BDT")}
                  </td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Hero empty state — brand-new workflow, nothing initialised yet
 * ───────────────────────────────────────────────────────────── */

function HeroEmptyState({ onBegin }: { onBegin: () => void }) {
  return (
    <div className="rounded-xl border border-(--border-subtle) bg-(--bg-surface) p-8 sm:p-12">
      <div className="mx-auto flex max-w-2xl flex-col items-center gap-4 text-center">
        <div className="flex size-12 items-center justify-center rounded-full bg-(--accent-soft) text-(--accent)">
          <Sparkles className="size-5" />
        </div>
        <h2 className="text-[22px] font-semibold tracking-tight text-(--text-primary)">
          Set up your opening balances
        </h2>
        <p className="text-[14px] leading-relaxed text-(--text-secondary)">
          Capture your business's position right before you started using
          BizOP — cash on hand, dealers who owe you, loans outstanding, etc.
          — so every report continues from where you left off.
        </p>
        <div className="flex flex-col items-center gap-2 pt-2">
          <Button onClick={onBegin}>
            Begin opening balance setup
            <ArrowRight className="size-4" />
          </Button>
          <p className="text-[12px] text-(--text-tertiary)">
            Defaults to today's date — change it inside the workflow if your
            books cut over from a different day.
          </p>
        </div>
        <ul className="mt-6 grid w-full grid-cols-1 gap-3 text-left sm:grid-cols-3">
          <HeroPoint icon={Building2} title="Receivables">
            Carry forward dealer balances so the AR aging report keeps its
            history.
          </HeroPoint>
          <HeroPoint icon={Briefcase} title="Payables & loans">
            Suppliers you owe, term loans still running — booked as opening
            credits.
          </HeroPoint>
          <HeroPoint icon={BadgeDollarSign} title="Cash + inventory">
            Bank, petty cash, stock-on-hand, prepaid rent — anything with a
            day-one balance.
          </HeroPoint>
        </ul>
      </div>
    </div>
  );
}

function HeroPoint({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <li className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-3">
      <div className="flex items-center gap-2">
        <Icon className="size-4 text-(--text-tertiary)" />
        <div className="text-[13px] font-medium text-(--text-primary)">
          {title}
        </div>
      </div>
      <p className="mt-1 text-[12px] text-(--text-secondary)">{children}</p>
    </li>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Unified chart-of-accounts opening balances
 *
 * Shows EVERY balance-sheet account (asset / liability / equity) from the
 * chart of accounts in a single flat list with opening debit / credit
 * columns. Replaces the old entity-split tabs as the primary editing
 * surface so the user sees the entire opening position in one place.
 * ───────────────────────────────────────────────────────────── */

type UnifiedRow = {
  account: MockAccount;
  existingCents: bigint;
};

function UnifiedAccountOpenings({
  accounts,
  existingRows,
  isDraft,
  onSetOpeningBalance,
}: {
  accounts: MockAccount[];
  existingRows: MockOpeningEntryRow[];
  isDraft: boolean;
  onSetOpeningBalance: (args: {
    accountId: string;
    balanceCents: bigint;
    asOfDate?: number;
    description?: string;
  }) => void;
}) {
  const [open, setOpen] = useState(false);

  // Build a lookup of existing opening balances by account id.
  const existingByAccountId = useMemo(() => {
    const map = new Map<string, bigint>();
    for (const r of existingRows) {
      if (r.category === "account") {
        map.set(r.accountId, (map.get(r.accountId) ?? 0n) + r.signedBaseCents);
      }
    }
    return map;
  }, [existingRows]);

  // All balance-sheet accounts (asset / liability / equity), sorted by code.
  const balanceSheetAccounts = useMemo<UnifiedRow[]>(() => {
    const allowed: MockAccountType[] = ["asset", "liability", "equity"];
    return accounts
      .filter((a) => allowed.includes(a.accountType) && a.isActive)
      .sort((a, b) => a.code.localeCompare(b.code))
      .map((a) => ({
        account: a,
        existingCents: existingByAccountId.get(a._id) ?? 0n,
      }));
  }, [accounts, existingByAccountId]);

  const [editingAccountId, setEditingAccountId] = useState<string | null>(null);
  const [editAmount, setEditAmount] = useState("");

  function startEdit(row: UnifiedRow) {
    setEditingAccountId(row.account._id);
    if (row.existingCents !== 0n) {
      // Convert cents to decimal for display
      const abs = row.existingCents < 0n ? -row.existingCents : row.existingCents;
      const sign = row.existingCents < 0n ? "-" : "";
      const whole = abs / 100n;
      const frac = abs % 100n;
      setEditAmount(`${sign}${whole}.${String(frac).padStart(2, "0")}`);
    } else {
      setEditAmount("");
    }
  }

  function saveEdit(accountId: string) {
    const cents = parseDecimalToCents(editAmount);
    if (cents === null && editAmount.trim() !== "") {
      toast.error("Enter a valid number, e.g. 250000 or -125000.");
      return;
    }
    onSetOpeningBalance({
      accountId,
      balanceCents: cents ?? 0n,
    });
    setEditingAccountId(null);
    setEditAmount("");
  }

  function cancelEdit() {
    setEditingAccountId(null);
    setEditAmount("");
  }

  const seededCount = balanceSheetAccounts.filter(
    (r) => r.existingCents !== 0n,
  ).length;

  return (
    <section className="rounded-lg border border-(--border-subtle) bg-(--bg-surface)">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center justify-between gap-3 px-4 py-3 text-left",
          "hover:bg-(--bg-hover)/50 transition-colors rounded-t-lg",
        )}
      >
        <div className="flex items-center gap-2.5">
          <BadgeDollarSign className="size-4 text-(--text-tertiary)" />
          <div>
            <div className="text-[13px] font-semibold tracking-tight text-(--text-primary)">
              Chart of Accounts — Opening Balances
            </div>
            <div className="text-[12px] text-(--text-tertiary)">
              Set opening debit / credit for every balance-sheet account in one
              place.{" "}
              {seededCount > 0
                ? `${seededCount} of ${balanceSheetAccounts.length} accounts have an opening balance.`
                : `${balanceSheetAccounts.length} accounts available.`}
            </div>
          </div>
        </div>
        <ChevronDown
          className={cn(
            "size-4 text-(--text-tertiary) transition-transform duration-(--duration-fast)",
            open ? "rotate-180" : "rotate-0",
          )}
        />
      </button>
      {open ? (
        <div className="border-t border-(--border-subtle) overflow-x-auto">
          {balanceSheetAccounts.length === 0 ? (
            <EmptyState
              compact
              icon={<BadgeDollarSign className="size-5" />}
              title="No balance-sheet accounts"
              description="Add accounts in Finance > Chart of Accounts first."
            />
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-(--border-subtle) bg-(--bg-canvas)">
                  <th className="px-4 py-2 text-left font-medium text-(--text-tertiary)">
                    Code
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-(--text-tertiary)">
                    Account
                  </th>
                  <th className="px-4 py-2 text-left font-medium text-(--text-tertiary)">
                    Type
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-(--text-tertiary)">
                    Opening Debit
                  </th>
                  <th className="px-4 py-2 text-right font-medium text-(--text-tertiary)">
                    Opening Credit
                  </th>
                  <th className="px-4 py-2 text-center font-medium text-(--text-tertiary)">
                    {isDraft ? "Edit" : ""}
                  </th>
                </tr>
              </thead>
              <tbody>
                {balanceSheetAccounts.map((row) => {
                  const isEditing = editingAccountId === row.account._id;
                  const debit =
                    row.existingCents > 0n ? row.existingCents : 0n;
                  const credit =
                    row.existingCents < 0n ? -row.existingCents : 0n;

                  return (
                    <tr
                      key={row.account._id}
                      className={cn(
                        "border-b border-(--border-subtle)/60",
                        row.existingCents !== 0n && "bg-(--bg-canvas)/50",
                      )}
                    >
                      <td className="px-4 py-2 font-mono text-(--text-secondary)">
                        {row.account.code}
                      </td>
                      <td className="px-4 py-2 text-(--text-primary)">
                        {row.account.name}
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant="neutral" size="sm">
                          {row.account.accountType}
                        </Badge>
                      </td>
                      {isEditing ? (
                        <>
                          <td colSpan={2} className="px-4 py-1.5">
                            <div className="flex items-center gap-2">
                              <Input
                                value={editAmount}
                                onChange={(e) => setEditAmount(e.target.value)}
                                inputMode="decimal"
                                placeholder={
                                  row.account.normalBalance === "debit"
                                    ? "Positive = debit"
                                    : "Negative = credit"
                                }
                                className="font-mono max-w-[200px]"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    saveEdit(row.account._id);
                                  }
                                  if (e.key === "Escape") cancelEdit();
                                }}
                              />
                              <div className="text-[11px] text-(--text-tertiary) whitespace-nowrap">
                                +ve = {row.account.normalBalance === "debit" ? "debit" : "credit"},
                                -ve = {row.account.normalBalance === "debit" ? "credit" : "debit"}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-1.5">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => saveEdit(row.account._id)}
                              >
                                <Check className="size-3.5" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={cancelEdit}
                              >
                                Cancel
                              </Button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="px-4 py-2 text-right font-mono tabular-nums text-(--text-primary)">
                            {debit > 0n ? formatMoney(debit, "BDT") : "—"}
                          </td>
                          <td className="px-4 py-2 text-right font-mono tabular-nums text-(--text-primary)">
                            {credit > 0n ? formatMoney(credit, "BDT") : "—"}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {isDraft ? (
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label={`Edit ${row.account.code}`}
                                onClick={() => startEdit(row)}
                              >
                                <Pencil className="size-3.5" />
                              </Button>
                            ) : null}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      ) : null}
    </section>
  );
}

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ───────────────────────────────────────────────────────────── */

function countNonEmptyTabs(
  buckets: Record<EntryTab["key"], EntryRow[]>,
): number {
  return (Object.keys(buckets) as EntryTab["key"][]).filter(
    (k) => buckets[k].length > 0,
  ).length;
}

function startOfDay(ms: number): number {
  const d = new Date(ms);
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

function toIsoDate(ms: number): string {
  const d = new Date(ms);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(d.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function fromIsoDate(iso: string): number {
  const [y, m, d] = iso.split("-").map((s) => Number(s));
  if (!y || !m || !d) return Number.NaN;
  return Date.UTC(y, m - 1, d);
}

/**
 * Parse a user-typed amount string into BigInt cents. Accepts:
 *   "250000"        → 25_000_000_00n
 *   "250000.50"     → 25_000_050_00n? no — 25_000_050n? careful: cents = major*100
 *   "-125000.25"    → -12_500_025n
 * Returns null on parse failure.
 */
function parseDecimalToCents(input: string): bigint | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const m = trimmed.match(/^(-)?(\d+)(?:\.(\d{1,2}))?$/);
  if (!m) return null;
  const sign = m[1] ? -1n : 1n;
  const whole = BigInt(m[2] ?? "0");
  const frac = m[3] ? m[3].padEnd(2, "0") : "00";
  const cents = whole * 100n + BigInt(frac);
  return sign * cents;
}

// Re-export for type symmetry with parent (kept here intentionally so this
// route is self-contained and doesn't pollute glMock with view-model types).
export type { EntryRow };

